import { action, observable, computed, makeObservable } from 'mobx';
import { CategoryModel } from '../model/CategoryModel';
import { i18n } from '../../i18n/i18n';
import { Category } from '../../web-api/domain/category';
import { ErrorReporter } from '../../errorHandling/ErrorReporter';
import { TemplatesStore } from './TemplatesStore';

const filterIds = ['ntt-exclusives', 'new', 'most-popular', 'blank'] as const;

export class CategoriesStore {
  private fetchedCategories: CategoryModel[];
  private expandedIds: string[] = [];
  private collapsedIds: string[] = [];

  constructor(
    private templatesStore: TemplatesStore,
    categories: Category[],
    private i18n: i18n,
    private errorReporter: ErrorReporter,
  ) {
    makeObservable<CategoriesStore, 'fetchedCategories' | 'expandedIds' | 'collapsedIds'>(this, {
      fetchedCategories: observable,
      expandedIds: observable,
      collapsedIds: observable,
      filters: computed,
      categories: computed,
      activeSubCategory: computed,
      activeCategory: computed,
      expandedCategories: computed,
      toggleCategory: action,
    });

    this.fetchedCategories = CategoriesStore.fromDTOs(categories);
  }

  get filters(): CategoryModel[] {
    let filters;

    try {
      filters = this.fetchedCategories.filter((category) => filterIds.includes(category.id as any));
      filters.sort((a, b) => filterIds.indexOf(a.id) - filterIds.indexOf(b.id));
    } catch (error) {
      this.errorReporter.reportError(error);
    }

    return filters;
  }

  get categories(): CategoryModel[] {
    let categories;

    try {
      categories = this.fetchedCategories
        .filter((category) => category.type !== 'sort')
        .map(({ items, ...category }) => ({
          ...category,
          items: items ? items.filter((category) => category.type !== 'sort') : [],
        }))
        .filter((category) => !filterIds.includes(category.id as any));
    } catch (error) {
      this.errorReporter.reportError(error);
    }

    return categories;
  }

  get activeSubCategory(): CategoryModel | undefined {
    if (this.templatesStore.loaded.appPage === 'subCategory') {
      return this.getCategoryBySlug(this.templatesStore.loaded.category, this.templatesStore.loaded.subCategory);
    }

    return undefined;
  }

  get activeCategory(): CategoryModel | undefined {
    const { loaded } = this.templatesStore;

    if (loaded.appPage === 'category' || loaded.appPage === 'subCategory') {
      return this.getCategoryBySlug(loaded.category);
    }

    if (loaded.appPage === 'home') {
      return this.getCategoryBySlug('all');
    }

    return undefined;
  }

  get expandedCategories(): CategoryModel[] {
    const states = {
      ...('category' in this.templatesStore.loaded && { [this.templatesStore.loaded.category]: true }),
      ...(this.expandedIds.length &&
        Object.assign.apply(
          null,
          this.expandedIds.map((id) => ({ [id]: true })),
        )),
      ...(this.collapsedIds.length &&
        Object.assign.apply(
          null,
          this.collapsedIds.map((id) => ({ [id]: false })),
        )),
    };
    const ids = Object.keys(states).filter((id) => states[id]);

    return ids.map((id) => this.getCategoryBySlug(id));
  }

  toggleCategory(categoryId: string, expand?: boolean) {
    const isExpanded =
      expand !== undefined
        ? !expand
        : this.collapsedIds.includes(categoryId)
        ? false
        : this.expandedIds.includes(categoryId) ||
          ('category' in this.templatesStore.loaded && this.templatesStore.loaded.category === categoryId);
    if (isExpanded) {
      this.expandedIds = this.expandedIds.filter((id) => id !== categoryId);
      this.collapsedIds.push(categoryId);
    } else {
      this.collapsedIds = this.collapsedIds.filter((id) => id !== categoryId);
      this.expandedIds.push(categoryId);
    }
  }

  public getCategoryBySlug(categorySlug: string, subCategorySlug?: string): CategoryModel {
    const category = [...this.categories, ...this.filters].find(({ id }) => id === categorySlug);
    if (category && subCategorySlug) {
      return category.items.find(({ id }) => id === subCategorySlug);
    }
    return category;
  }

  private static fromDTOs(categories: Category[]): CategoryModel[] {
    return categories.map(CategoryModel.fromDTO);
  }

  // @todo: it doesn't actually return Category[], need to fix it
  private toDTOs(models: CategoryModel[]): Category[] {
    return models.map((model) => CategoryModel.toDTO(model));
  }

  public serialize(): Category[] {
    return this.toDTOs(this.filters.concat(this.categories));
  }
}
